import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import {
  addToast,
  Button,
  Checkbox,
  Chip,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { useDebouncedValue } from "@mantine/hooks";
import { Plus, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { ManageCustomerModal } from "@/Components/NextBase/Customer/ManageCustomerBasicInfoModal";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { LimeSelectInput } from "@/Components/NextBase/LimeSelectInput";
import { api } from "@/lib/api-client";
import { CustomerSubscriptionDrawer } from "@/pages/Dashboard/Customers/CustomerPage/components/Subscriptions/CustomerSubscriptionDrawer";
import {
  getApplicableSubscriptions,
  getNewCustomerPrefill,
  getRemainingCredits,
  getSubscriptionLabel,
} from "./fixedMemberHelpers";

const ADD_NEW_CUSTOMER_KEY = "add-new-customer";

type SelectedCustomer = {
  customerId: number;
  name: string;
  lastName: string;
  email?: string | null;
  gsm?: string | null;
};

export function AddFixedMemberModal({
  isOpen,
  onClose,
  fixedAppointmentId,
  serviceId,
  presetCustomer,
  onAdded,
}: {
  isOpen: boolean;
  onClose: () => void;
  fixedAppointmentId: number;
  // When omitted, applicable subscriptions aren't filtered by service.
  serviceId?: number;
  // When provided, the customer is fixed and the search UI is skipped.
  presetCustomer?: SelectedCustomer;
  onAdded?: () => void;
}) {
  const [customer, setCustomer] = useState<SelectedCustomer | null>(
    presetCustomer ?? null,
  );
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [customerSubscriptionId, setCustomerSubscriptionId] = useState<
    number | null
  >(null);
  const [rebookCancelledOccurrences, setRebookCancelledOccurrences] =
    useState(false);
  const [isCreateCustomerOpen, setIsCreateCustomerOpen] = useState(false);
  const [createdCustomerId, setCreatedCustomerId] = useState<number | null>(
    null,
  );
  const [isSubscriptionDrawerOpen, setIsSubscriptionDrawerOpen] =
    useState(false);
  const [pendingSubscriptionId, setPendingSubscriptionId] = useState<
    number | null
  >(null);

  // Reset to the initial state each time the modal opens.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) {
      setCustomer(presetCustomer ?? null);
      setSearch("");
      setCustomerSubscriptionId(null);
      setRebookCancelledOccurrences(false);
      setCreatedCustomerId(null);
      setPendingSubscriptionId(null);
      setIsCreateCustomerOpen(false);
      setIsSubscriptionDrawerOpen(false);
    }
  }

  const { data: bookingPreview } =
    api.fixedAppointment.useGetFixedAppointmentMemberBookingPreview({
      fixedAppointmentId,
      customerId: customer?.customerId ?? null,
      enabled: isOpen,
    });

  const previouslyCancelledCount =
    bookingPreview?.previouslyCancelledCount ?? 0;

  const { data: customerSearchData, isFetching: isSearchingCustomers } =
    api.customer.useGetCustomers({
      searchQuery: debouncedSearch,
      pagination: { limit: 20, offset: 0 },
      enabled: isOpen && debouncedSearch.trim().length >= 2,
    });

  const canOfferNewCustomer =
    debouncedSearch.trim().length >= 2 && !isSearchingCustomers;

  const customerOptions = useMemo(() => {
    const options = (customerSearchData?.customers ?? []).map((c) => ({
      key: String(c.customerId),
      label: (
        <div className="flex flex-col">
          <span className="text-sm">
            {c.name} {c.lastName}
          </span>
          <span className="text-default-500 text-xs">
            {[c.email, c.gsm].filter(Boolean).join(" ")}
          </span>
        </div>
      ),
    }));
    if (!canOfferNewCustomer) return options;
    return [
      ...options,
      {
        key: ADD_NEW_CUSTOMER_KEY,
        label: (
          <div className="flex items-center gap-2 text-sm">
            <Plus size={16} />
            <Trans>Dodaj novo stranko</Trans>
          </div>
        ),
      },
    ];
  }, [customerSearchData, canOfferNewCustomer]);

  const { data: createdCustomer } = api.customer.useGetCustomer({
    customerId: createdCustomerId ?? undefined,
    enabled: isOpen && createdCustomerId != null,
  });

  if (
    createdCustomerId != null &&
    createdCustomer?.customerId === createdCustomerId
  ) {
    setCreatedCustomerId(null);
    selectCustomer({
      customerId: createdCustomer.customerId,
      name: createdCustomer.name,
      lastName: createdCustomer.lastName,
      email: createdCustomer.email,
      gsm: createdCustomer.gsm,
    });
  }

  const { mutateAsync: addMember, isPending } =
    api.fixedAppointment.usePostFixedAppointmentMember();

  const { data: subscriptionsData, isLoading: isLoadingSubscriptions } =
    api.customer.useGetCustomerSubscriptionsForCustomer(
      {
        customerId: customer?.customerId ?? -1,
        status: "all",
      },
      { enabled: isOpen },
    );

  const applicableSubscriptions = useMemo(() => {
    if (!customer) return [];
    return getApplicableSubscriptions(
      subscriptionsData?.customerSubscriptions ?? [],
      serviceId,
    );
  }, [subscriptionsData, customer, serviceId]);

  if (
    pendingSubscriptionId != null &&
    applicableSubscriptions.some((sub) => sub.id === pendingSubscriptionId)
  ) {
    setPendingSubscriptionId(null);
    setCustomerSubscriptionId(pendingSubscriptionId);
  }

  const needsPackage =
    customer != null &&
    !isLoadingSubscriptions &&
    applicableSubscriptions.length === 0;

  function selectCustomer(next: SelectedCustomer | null) {
    setCustomer(next);
    setCustomerSubscriptionId(null);
    setRebookCancelledOccurrences(false);
    setPendingSubscriptionId(null);
  }

  function toggleSubscription(subscriptionId: number) {
    setCustomerSubscriptionId((prev) =>
      prev === subscriptionId ? null : subscriptionId,
    );
  }

  async function handleSubmit() {
    if (!customer) return;
    try {
      const {
        bookedCount,
        coveredBySubscriptionCount,
        previouslyCancelledCount: cancelledOccurrenceCount,
      } = await addMember({
        fixedAppointmentId,
        body: {
          customerId: customer.customerId,
          bookFutureOccurrences: true,
          rebookCancelledOccurrences,
          customerSubscriptionId,
        },
      });
      const skippedCancelledCount = rebookCancelledOccurrences
        ? 0
        : cancelledOccurrenceCount;
      const bookedDescription =
        bookedCount > 0
          ? coveredBySubscriptionCount > 0
            ? t`Vpisana v ${bookedCount} prihodnjih terminov (${coveredBySubscriptionCount} kritih s paketom).`
            : t`Vpisana v ${bookedCount} prihodnjih terminov.`
          : t`Ni prihodnjih terminov za vpis.`;
      addToast({
        title: t`Stranka je dodana kot redna`,
        description:
          skippedCancelledCount > 0
            ? `${bookedDescription} ${t`Izpuščenih ${skippedCancelledCount} terminov, ki jih je stranka predhodno odpovedala.`}`
            : bookedDescription,
        color: "success",
      });
      onAdded?.();
      onClose();
    } catch {
      addToast({
        title: t`Napaka pri dodajanju redne stranke`,
        color: "danger",
      });
    }
  }

  return (
    <>
      <LimeModal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalContent>
          <ModalHeader>
            <Trans>Dodaj redno stranko</Trans>
          </ModalHeader>
          <ModalBody>
            <div className="flex flex-col gap-3">
              {customer ? (
                <div
                  data-identifier="fixed-appt-selected-customer"
                  className="border-divider flex items-center justify-between gap-2 rounded-lg border px-3 py-2"
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">
                      {customer.name} {customer.lastName}
                    </span>
                    <span className="text-default-500 text-xs">
                      {customer.email ?? customer.gsm ?? ""}
                    </span>
                  </div>
                  {!presetCustomer && (
                    <Button
                      size="sm"
                      variant="light"
                      onPress={() => selectCustomer(null)}
                    >
                      <Trans>Spremeni</Trans>
                    </Button>
                  )}
                </div>
              ) : (
                <LimeSelectInput
                  inputType="text"
                  inputProps={{
                    "data-identifier": "fixed-appt-customer-search",
                    label: t`Stranka`,
                    placeholder: t`Poišči stranko po imenu, e-pošti ali telefonu...`,
                    value: search,
                    onValueChange: setSearch,
                    startContent: <Search size={16} />,
                    isLoading: isSearchingCustomers,
                    autoFocus: true,
                  }}
                  items={customerOptions}
                  onSelect={(key) => {
                    if (key === ADD_NEW_CUSTOMER_KEY) {
                      setIsCreateCustomerOpen(true);
                      return;
                    }
                    const picked = customerSearchData?.customers.find(
                      (c) => String(c.customerId) === key,
                    );
                    if (picked) selectCustomer(picked);
                  }}
                />
              )}

              {customer && applicableSubscriptions.length > 0 && (
                <div className="flex flex-col gap-2">
                  <span className="text-default-600 text-sm font-medium">
                    <Trans>Uporabi paket</Trans>
                  </span>
                  {applicableSubscriptions.map((sub) => {
                    const remainingCredits = getRemainingCredits(sub);
                    return (
                      <div
                        key={sub.id}
                        className="border-divider flex items-center justify-between gap-2 rounded-lg border px-3 py-2"
                      >
                        <Checkbox
                          data-identifier="fixed-appt-subscription-option"
                          isSelected={customerSubscriptionId === sub.id}
                          onValueChange={() => toggleSubscription(sub.id)}
                          size="sm"
                        >
                          <div className="flex flex-col">
                            <span className="text-sm">
                              {getSubscriptionLabel(sub) ?? t`Paket po meri`}
                            </span>
                            <span className="text-default-500 text-xs">
                              {sub.validFromFormatted} – {sub.validToFormatted}
                            </span>
                          </div>
                        </Checkbox>
                        {remainingCredits > 0 && (
                          <Chip size="sm" variant="flat" color="secondary">
                            <Trans>+{remainingCredits} kreditov</Trans>
                          </Chip>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {needsPackage && (
                <div className="flex flex-col gap-2">
                  <LimeAlert
                    color="warning"
                    message={t`Stranka nima paketa, ki bi pokrival storitev tega rednega termina.`}
                  />
                  <Button
                    data-identifier="fixed-appt-assign-package"
                    className="self-start"
                    color="primary"
                    variant="flat"
                    size="sm"
                    startContent={<Plus size={16} />}
                    onPress={() => setIsSubscriptionDrawerOpen(true)}
                  >
                    <Trans>Dodeli stranki paket</Trans>
                  </Button>
                </div>
              )}

              <LimeAlert
                color="secondary"
                message={t`Stranka bo dodana na vse prihodnje termine tega rednega termina.`}
              />

              {customer && previouslyCancelledCount > 0 && (
                <div className="flex flex-col gap-2">
                  <LimeAlert
                    color="warning"
                    message={
                      rebookCancelledOccurrences
                        ? t`Stranka je predhodno odpovedala ${previouslyCancelledCount} prihodnjih terminov. Vpisana bo tudi nanje.`
                        : t`Stranka je predhodno odpovedala ${previouslyCancelledCount} prihodnjih terminov. Ti termini bodo izpuščeni.`
                    }
                  />
                  <Checkbox
                    isSelected={rebookCancelledOccurrences}
                    onValueChange={setRebookCancelledOccurrences}
                    size="sm"
                  >
                    <Trans>
                      Stranko vseeno vpiši tudi na predhodno odpovedane termine.
                    </Trans>
                  </Checkbox>
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={onClose}>
              <Trans>Prekliči</Trans>
            </Button>
            <Button
              data-identifier="fixed-appt-add-member-submit"
              color="primary"
              onPress={handleSubmit}
              isDisabled={!customer}
              isLoading={isPending}
            >
              <Trans>Dodaj</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </LimeModal>

      <ManageCustomerModal
        isOpen={isCreateCustomerOpen}
        handleClose={({ customerId }) => {
          setIsCreateCustomerOpen(false);
          if (customerId != null) setCreatedCustomerId(customerId);
        }}
        restrictToEntityType="physical"
        showRedirectToCustomerCardButton={false}
        initialValues={getNewCustomerPrefill(search)}
      />

      {customer && (
        <CustomerSubscriptionDrawer
          isOpen={isSubscriptionDrawerOpen}
          onOpenChange={() => setIsSubscriptionDrawerOpen(false)}
          customerId={customer.customerId}
          onCreated={(subscription) =>
            setPendingSubscriptionId(subscription.id)
          }
        />
      )}
    </>
  );
}
